import { createAsyncThunk } from '@reduxjs/toolkit';
import focusBearApi from 'services/axios-config';
import Endpoints from 'constants/endpoints';
import {
  DAY_PLAN_IMPORT_EXTRACT_TIMEOUT_MS,
  DAY_PLAN_IMPORT_FAILED_KEY
} from 'constants/day-plan';
import { HTTP_STATS_CODE, PAGE } from 'constants/general';
import { SORT_MODE, SORT_ORDER, TO_DO_STATUS } from 'enums';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import { ToDoTask } from 'interfaces/common.interface';
import {
  CalendarEvent,
  CommitFileImportArgs,
  CommitFileImportResult,
  CreateDayPlanEventsPayload,
  CreateDayPlanEventsResponse,
  CreateEventSeriesPayload,
  CreateEventSeriesResponse,
  DayPlanDismissedEventArgs,
  DayPlanEventsForDayResponse,
  DayPlanResponse,
  DayPlanTaskCatalogPage,
  DeleteEventSeriesArgs,
  EventColorPayload,
  EventColorsResponse,
  EventSeriesListResponse,
  EventSeriesOccurrencesResponse,
  FetchCalendarEventsArgs,
  FetchCalendarPlatformAccountArgs,
  FetchRoutineProgressArgs,
  FileImportExtractRequest,
  FileImportResponse,
  ImportFileFromKeyArgs,
  PlanTaskArgs,
  PlatformCalendarFetchResult,
  TodayRoutineProgress
} from 'interfaces/day-plan.interface';
import { setFileImportCommitProgress } from './slice';
import {
  buildCalendarEventsParams,
  buildCreateDayPlanEventItems,
  buildCreateEventSeriesItems,
  buildPlanningPatchPayload,
  dedupeEventsByExternalId,
  filterEventsBySelectedCalendarIds,
  normalizeEventColoursResponse
} from 'utils/day-plan/index.util';
import { getNowLocalIso } from 'utils/time.util';
import { refineTaskTopMetrics } from 'utils/todo.util';
import type { RootState } from 'store';

export const fetchDayPlan = createAsyncThunk(
  'day_plan/fetch_day',
  async (day: string, { rejectWithValue }) => {
    try {
      const { data } = await focusBearApi.get<DayPlanResponse>(
        Endpoints.DAY_PLAN_DAY(day)
      );
      return data;
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_load_day'));
      return rejectWithValue(day);
    }
  }
);

export const fetchRoutineProgress = createAsyncThunk(
  'day_plan/fetch_routine_progress',
  async ({ date, timezone }: FetchRoutineProgressArgs, { rejectWithValue }) => {
    try {
      const { data } = await focusBearApi.get<TodayRoutineProgress>(
        Endpoints.ROUTINE_PROGRESS,
        { params: { date, timezone } }
      );
      return data;
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_load_routine_progress'));
      return rejectWithValue(date);
    }
  }
);

export const fetchEventColours = createAsyncThunk(
  'day_plan/fetch_event_colours',
  async () => {
    try {
      const { data } = await focusBearApi.get<EventColorsResponse>(
        Endpoints.DAY_PLAN_EVENT_COLOUR
      );
      return normalizeEventColoursResponse(data);
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_load_event_colours'));
      return null;
    }
  }
);

const fetchEventsForPlatformAccount = async ({
  platform,
  account,
  day
}: FetchCalendarPlatformAccountArgs): Promise<PlatformCalendarFetchResult> => {
  try {
    const { data } = await focusBearApi.get<CalendarEvent[]>(
      Endpoints.CALENDAR_EVENTS(platform),
      { params: buildCalendarEventsParams({ account, day }) }
    );
    return {
      events: (data ?? []).map((event) => ({ ...event, platform })),
      didFail: false
    };
  } catch (error) {
    captureException(error);
    return { events: [], didFail: true };
  }
};

export const fetchCalendarEvents = createAsyncThunk(
  'day_plan/fetch_calendar_events',
  async ({
    day,
    platforms,
    accountsByPlatform,
    selectedCalendarIds
  }: FetchCalendarEventsArgs) => {
    const requests: Promise<PlatformCalendarFetchResult>[] = [];
    platforms.forEach((platform) => {
      const accounts = accountsByPlatform[platform] ?? [];
      accounts.forEach((account) => {
        requests.push(
          fetchEventsForPlatformAccount({ platform, account, day })
        );
      });
    });
    if (requests.length === 0) {
      return [];
    }
    const results = await Promise.all(requests);
    if (results.every((result) => result.didFail)) {
      toast.error(t('day_plan.could_not_load_events'));
    }
    return filterEventsBySelectedCalendarIds(
      dedupeEventsByExternalId(results.flatMap((result) => result.events)),
      selectedCalendarIds
    );
  }
);

/**
 * Plan / timebox / reorder / unplan via `PATCH /to-do/:id` with a partial body.
 * Omit fields you do not intend to change (reorder must not send block key).
 */
export const planTask = createAsyncThunk(
  'day_plan/plan_task',
  async (args: PlanTaskArgs) => {
    try {
      const patch = buildPlanningPatchPayload(args);
      const { data, status } = await focusBearApi.patch<ToDoTask>(
        Endpoints.TO_DO_BY_ID(args.task.id),
        patch
      );
      if (status !== HTTP_STATS_CODE.SUCCESS) {
        toast.error(t('day_plan.could_not_update_plan'));
        return null;
      }
      return data ? { ...args.task, ...data } : { ...args.task, ...patch };
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_update_plan'));
      return null;
    }
  }
);

export const dismissEvent = createAsyncThunk(
  'day_plan/dismiss_event',
  async ({ day, eventId }: DayPlanDismissedEventArgs) => {
    try {
      await focusBearApi.put(Endpoints.DAY_PLAN_DISMISSED_EVENT(day, eventId));
      return eventId;
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_dismiss_event'));
      return null;
    }
  }
);

export const undismissEvent = createAsyncThunk(
  'day_plan/undismiss_event',
  async ({ day, eventId }: DayPlanDismissedEventArgs) => {
    try {
      await focusBearApi.delete(
        Endpoints.DAY_PLAN_DISMISSED_EVENT(day, eventId)
      );
      return eventId;
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_restore_event'));
      return null;
    }
  }
);

export const setEventColor = createAsyncThunk(
  'day_plan/set_event_color',
  async (payload: EventColorPayload) => {
    try {
      await focusBearApi.put(Endpoints.DAY_PLAN_EVENT_COLOUR, payload);
      return payload;
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_set_colour'));
      return null;
    }
  }
);

/** Send an uploaded file key for extraction; never writes anything. */
export const importFileFromKey = createAsyncThunk(
  'day_plan/import_file',
  async (
    { key, repeatedEventsEndOn }: ImportFileFromKeyArgs,
    { rejectWithValue }
  ) => {
    try {
      const body: FileImportExtractRequest = {
        key,
        client_datetime_iso: getNowLocalIso()
      };
      if (repeatedEventsEndOn !== undefined && repeatedEventsEndOn.length > 0) {
        body.repeated_events_end_on = repeatedEventsEndOn;
      }
      const { data } = await focusBearApi.post<FileImportResponse>(
        Endpoints.DAY_PLAN_FILE_IMPORT,
        body,
        { timeout: DAY_PLAN_IMPORT_EXTRACT_TIMEOUT_MS }
      );
      return {
        unscheduled_entries: data?.unscheduled_entries ?? [],
        events: data?.events ?? [],
        event_series: data?.event_series ?? [],
        truncated: data?.truncated === true
      };
    } catch (error) {
      captureException(error);
      return rejectWithValue(DAY_PLAN_IMPORT_FAILED_KEY);
    }
  }
);

export const fetchDayPlanOneOffEvents = createAsyncThunk(
  'day_plan/fetch_one_off_events',
  async (day: string, { rejectWithValue }) => {
    try {
      const { data } = await focusBearApi.get<DayPlanEventsForDayResponse>(
        Endpoints.DAY_PLAN_EVENTS_DAY(day)
      );
      return {
        day: data?.day ?? day,
        events: data?.events ?? []
      };
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_load_day_plan_events'));
      return rejectWithValue(day);
    }
  }
);

export const fetchEventSeriesForDay = createAsyncThunk(
  'day_plan/fetch_event_series_day',
  async (day: string, { rejectWithValue }) => {
    try {
      const { data } = await focusBearApi.get<EventSeriesOccurrencesResponse>(
        Endpoints.DAY_PLAN_EVENT_SERIES_DAY(day)
      );
      return {
        day: data?.day ?? day,
        events: data?.events ?? []
      };
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_load_event_series'));
      return rejectWithValue(day);
    }
  }
);

export const fetchEventSeriesList = createAsyncThunk(
  'day_plan/fetch_event_series_list',
  async () => {
    try {
      const { data } = await focusBearApi.get<EventSeriesListResponse>(
        Endpoints.DAY_PLAN_EVENT_SERIES
      );
      return data?.series ?? [];
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_load_event_series_list'));
      return [];
    }
  }
);

export const deleteEventSeries = createAsyncThunk(
  'day_plan/delete_event_series',
  async (
    { seriesId, day }: DeleteEventSeriesArgs,
    { dispatch }
  ): Promise<boolean> => {
    try {
      await focusBearApi.delete(
        Endpoints.DAY_PLAN_EVENT_SERIES_BY_ID(seriesId)
      );
      await dispatch(fetchEventSeriesForDay(day));
      toast.success(t('day_plan.event_series_deleted'));
      return true;
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_delete_event_series'));
      return false;
    }
  }
);

export const commitFileImport = createAsyncThunk(
  'day_plan/commit_file_import',
  async (
    { day, timezone, events, eventSeries = [] }: CommitFileImportArgs,
    { dispatch }
  ): Promise<CommitFileImportResult> => {
    const total = eventSeries.length + events.length;
    let completed = 0;
    let failureCount = 0;

    if (eventSeries.length > 0) {
      failureCount += await commitImportEventSeries({
        eventSeries,
        timezone
      });
      completed += eventSeries.length;
      dispatch(setFileImportCommitProgress({ completed, total }));
    }

    if (events.length > 0) {
      failureCount += await commitImportOneOffEvents({ events, timezone });
      completed += events.length;
      dispatch(setFileImportCommitProgress({ completed, total }));
    }

    if (failureCount > 0) {
      toast.error(t('day_plan.file_import_partial_failed'));
      return { success: false };
    }

    toast.success(t('day_plan.file_import_added'));
    await Promise.allSettled([
      dispatch(fetchDayPlan(day)),
      dispatch(fetchEventSeriesForDay(day)),
      dispatch(fetchDayPlanOneOffEvents(day))
    ]);
    return { success: true };
  }
);

const commitImportEventSeries = async ({
  eventSeries,
  timezone
}: {
  eventSeries: CommitFileImportArgs['eventSeries'];
  timezone: string;
}): Promise<number> => {
  const seriesRows = eventSeries ?? [];
  try {
    const seriesEvents = buildCreateEventSeriesItems({
      seriesRows,
      timezone
    });
    if (seriesEvents.length !== seriesRows.length) {
      return seriesRows.length;
    }
    const body: CreateEventSeriesPayload = { events: seriesEvents };
    await focusBearApi.post<CreateEventSeriesResponse>(
      Endpoints.DAY_PLAN_EVENT_SERIES,
      body
    );
    return 0;
  } catch (error) {
    captureException(error);
    return seriesRows.length;
  }
};

const commitImportOneOffEvents = async ({
  events,
  timezone
}: {
  events: CommitFileImportArgs['events'];
  timezone: string;
}): Promise<number> => {
  try {
    const items = buildCreateDayPlanEventItems({ events, timezone });
    if (items.length !== events.length) {
      return events.length;
    }
    const body: CreateDayPlanEventsPayload = { events: items };
    await focusBearApi.post<CreateDayPlanEventsResponse>(
      Endpoints.DAY_PLAN_EVENTS,
      body
    );
    return 0;
  } catch (error) {
    captureException(error);
    return events.length;
  }
};

const fetchTaskCatalogPage = async (
  sortMode: SORT_MODE
): Promise<DayPlanTaskCatalogPage> => {
  const { data } = await focusBearApi.get(Endpoints.TO_DO, {
    params: {
      page: PAGE.FIRST,
      take: PAGE.TAKE.DAY_PLAN_TASK_CATALOG,
      order: SORT_ORDER.DESC,
      sort_mode: sortMode,
      status: TO_DO_STATUS.NOT_STARTED
    }
  });
  const rawList: ToDoTask[] = data?.data ?? [];
  return {
    tasks: rawList.map((task) => refineTaskTopMetrics(task))
  };
};

/** First page of the Day Plan todo catalog — does not touch `toDo.tasks`. */
export const fetchDayPlanTaskCatalog = createAsyncThunk(
  'day_plan/fetch_task_catalog',
  async (sortModeOverride: SORT_MODE | undefined = undefined, { getState }) => {
    const state = getState() as RootState;
    const sortMode =
      sortModeOverride ?? state.dayPlan.taskCatalogSortMode ?? SORT_MODE.SMART;
    try {
      return await fetchTaskCatalogPage(sortMode);
    } catch (error) {
      captureException(error);
      toast.error(t('day_plan.could_not_load_tasks'));
      return {
        tasks: []
      };
    }
  }
);
